import * as React from 'react'
import { memo, useCallback, useEffect, useState } from 'react'
import { X } from 'lucide-react'

import { Markdown } from '../markdown/Markdown'

/**
 * A node's answer, rendered once per answer rather than once per render.
 *
 * The canvas re-renders whenever anything on it changes — a menu opening, a
 * pointer crossing it — and the nodes are memoised, but they are handed inline
 * callbacks, so `memo` misses every time. That was invisible until an answer
 * contained a picture: re-rendering the markdown builds new `<img>` elements,
 * the browser treats them as new images and fetches them again, and the pictures
 * in every node blink each time the cursor moves. This takes the text and two
 * stable handlers, so `memo` holds and the DOM under it is left alone.
 *
 * Shared by the app and the share viewer. Everything Electron-specific — opening
 * a URL, opening a file, dragging an image into a composer — arrives as a prop,
 * so a browser build can render exactly the same answer without them.
 */
export interface AirAnswerProps {
  text: string
  /** Follow a link. Omitted means links are inert. */
  onUrlClick?: (url: string) => void
  /** Open a file path named in the answer. Omitted means file links are inert. */
  onFileClick?: (path: string) => void
  /**
   * Let an answer's images be dragged out — the app attaches them to the Prompt
   * Input. Off by default: a reader has no composer to drag into.
   */
  draggableImages?: boolean
}

/** The disk path behind an answer image's rendered src, when there is one, so it
 *  can be dragged into the composer. Covers the thumbnail scheme the app uses, a
 *  file URL, and a bare absolute path; anything else (a remote/data URL) yields
 *  null and simply is not draggable. */
function pathFromImgSrc(src: string): string | null {
  if (src.startsWith('thumbnail://')) {
    try { return decodeURIComponent(src.split('/').pop() || '') || null } catch { return null }
  }
  if (src.startsWith('file://')) {
    try { return decodeURIComponent(new URL(src).pathname) || null } catch { return null }
  }
  return src.startsWith('/') ? src : null
}

// Delegated, like the click handler: the images are the markdown renderer's, so
// there is no element of ours to make draggable.
const onImgDragStart = (e: React.DragEvent) => {
  const el = e.target as HTMLElement
  if (el.tagName !== 'IMG') return
  const path = pathFromImgSrc((el as HTMLImageElement).getAttribute('src') || '')
  if (!path) return
  e.dataTransfer.setData('application/x-synti-path', path)
  e.dataTransfer.setData('text/plain', path)
  e.dataTransfer.effectAllowed = 'copy'
}

function AirAnswerInner({ text, onUrlClick, onFileClick, draggableImages }: AirAnswerProps) {
  /**
   * The picture being read at full size, if one is.
   *
   * A node is a fixed-width card on a plane, so a picture in it is always
   * smaller than the picture — and there was no way to see the rest of it. The
   * state lives here rather than on the node: this component is memoised on the
   * text alone, so opening a picture cannot re-render the canvas around it.
   */
  const [open, setOpen] = useState<{ src: string; alt: string } | null>(null)

  // Delegated, because the images are produced by the markdown renderer and
  // there is no element of ours to hang a handler on.
  const onClick = useCallback((e: React.MouseEvent) => {
    const el = e.target as HTMLElement
    if (el.tagName !== 'IMG') return
    e.preventDefault()
    e.stopPropagation()
    const img = el as HTMLImageElement
    if (img.src) setOpen({ src: img.src, alt: img.alt })
  }, [])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return
      e.preventDefault()
      e.stopPropagation()
      setOpen(null)
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open])

  return (
    <>
      <div
        onClick={onClick}
        {...(draggableImages ? { onDragStart: onImgDragStart } : {})}
        className="[&_img]:cursor-zoom-in"
      >
        {/* `full`, not `minimal`: a chat node often holds a whole code review, and
            minimal mode dropped its tables to plain rows and its typography to bare
            text. Full mode brings the styled tables, code blocks and spacing that
            make it read like the GitHub review it mirrors. */}
        <Markdown
          mode="full"
          {...(onUrlClick ? { onUrlClick } : {})}
          {...(onFileClick ? { onFileClick } : {})}
        >
          {text}
        </Markdown>
      </div>

      {open && (
        <div
          data-no-dnd="true"
          data-air-overlay="true"
          className="animate-in fade-in-0 fixed inset-0 z-overlay flex flex-col items-center justify-center gap-4 bg-black/80 p-8 duration-200"
          onClick={(e) => {
            e.stopPropagation()
            setOpen(null)
          }}
        >
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation()
              setOpen(null)
            }}
            className="absolute right-4 top-4 rounded-lg p-2 text-white/70 transition-colors hover:bg-white/10 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
          <img
            src={open.src}
            alt={open.alt}
            className="animate-in zoom-in-95 max-h-[85vh] max-w-[85vw] rounded-lg object-contain shadow-strong duration-200"
            onClick={(e) => e.stopPropagation()}
            draggable={false}
          />
          {/* What the picture was described as, where there is room to read it.
              In the node the caption would push the conversation down for
              something you can see; here it is the only place it fits. */}
          {open.alt && (
            <span className="max-w-[70vw] truncate text-[12px] text-white/60">{open.alt}</span>
          )}
        </div>
      )}
    </>
  )
}

export const AirAnswer = memo(AirAnswerInner)
